import { CheckCircle2Icon, ExternalLinkIcon, KeyRoundIcon, ShieldCheckIcon } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { CopyButton } from '@/components/CopyButton';
import { ErrorState } from '@/components/ErrorState';
import { LoadingBlock } from '@/components/LoadingBlock';
import { TagInput } from '@/components/TagInput';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Field } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { ProviderIcon, SSO_LABEL } from '@/features/auth/SsoButtons';
import { errorMessage } from '@/lib/api';
import type { MfaMode, SsoMode, SsoProviderConfigDto, SsoProviderId, TenantSettingsPatch, TenantSettingsView } from '@/lib/types';
import { useTenantSso, useUpdateTenantSso } from './queries';

type SaveFn = (patch: TenantSettingsPatch, okMessage?: string) => Promise<void>;

const selectClass = 'border-input bg-background h-9 w-full border px-3 text-sm';

function Section({ title, description, children, footer }: { title: ReactNode; description?: ReactNode; children: ReactNode; footer?: ReactNode }) {
  return (
    <section className="bg-card border">
      <div className="border-b px-5 py-4">
        <h2 className="text-sm font-semibold">{title}</h2>
        {description && <p className="text-muted-foreground mt-1 text-sm">{description}</p>}
      </div>
      <div className="grid gap-4 px-5 py-4">{children}</div>
      {footer && <div className="flex items-center justify-end gap-2 border-t px-5 py-3">{footer}</div>}
    </section>
  );
}

function ModeRadio<T extends string>({ value, onChange, options, name }: { value: T; onChange: (v: T) => void; options: Array<{ value: T; label: string; hint: string }>; name: string }) {
  return (
    <RadioGroup value={value} onValueChange={(v) => onChange(v as T)} className="grid gap-2 sm:grid-cols-3">
      {options.map((o) => (
        <label key={o.value} htmlFor={`${name}-${o.value}`} className={`flex cursor-pointer items-start gap-3 border p-3 ${value === o.value ? 'border-foreground/40 bg-muted/40' : ''}`}>
          <RadioGroupItem id={`${name}-${o.value}`} value={o.value} className="mt-0.5" />
          <span>
            <span className="block text-sm font-medium">{o.label}</span>
            <span className="text-muted-foreground block text-xs leading-relaxed">{o.hint}</span>
          </span>
        </label>
      ))}
    </RadioGroup>
  );
}

/** Organization → Settings → Sign-in: single sign-on providers and policy, two-step verification policy. */
export function SignInTab({ view, onSave, busy }: { view: TenantSettingsView; onSave: SaveFn; busy: boolean }) {
  const sso = useTenantSso();
  if (sso.isPending) return <LoadingBlock rows={4} />;
  if (sso.isError) return <ErrorState error={sso.error} onRetry={() => void sso.refetch()} />;
  return (
    <div className="grid gap-6">
      <SsoSection view={view} providers={sso.data.providers} onSave={onSave} busy={busy} />
      <MfaSection view={view} onSave={onSave} busy={busy} />
    </div>
  );
}

// ---------------------------------------------------------------- single sign-on

const SSO_MODES: Array<{ value: SsoMode; label: string; hint: string }> = [
  { value: 'off', label: 'Off', hint: 'People sign in with the methods the platform enabled for you.' },
  { value: 'optional', label: 'Offered', hint: '"Continue with Microsoft / Google" buttons appear beside the other methods.' },
  { value: 'required', label: 'Required', hint: 'Single sign-on is the only way in. Passwords and one-time codes are switched off.' },
];

function SsoSection({ view, providers, onSave, busy }: { view: TenantSettingsView; providers: SsoProviderConfigDto[]; onSave: SaveFn; busy: boolean }) {
  const policy = view.settings.auth.sso;
  const [mode, setMode] = useState<SsoMode>(policy.mode);
  const [domains, setDomains] = useState<string[]>(policy.allowedDomains);
  const [autoProvision, setAutoProvision] = useState(policy.autoProvision);
  const [defaultRole, setDefaultRole] = useState(policy.defaultRole);
  const [mfaExempt, setMfaExempt] = useState(policy.mfaExempt);
  useEffect(() => {
    setMode(policy.mode);
    setDomains(policy.allowedDomains);
    setAutoProvision(policy.autoProvision);
    setDefaultRole(policy.defaultRole);
    setMfaExempt(policy.mfaExempt);
  }, [policy.mode, policy.allowedDomains, policy.autoProvision, policy.defaultRole, policy.mfaExempt]);
  const dirty = mode !== policy.mode || domains.join(',') !== policy.allowedDomains.join(',') || autoProvision !== policy.autoProvision || defaultRole !== policy.defaultRole || mfaExempt !== policy.mfaExempt;
  const anyEnabled = providers.some((p) => p.enabled && p.configured);

  return (
    <Section
      title="Single sign-on"
      description="Let people sign in with the Microsoft or Google account they already use at work. Turn a provider on below, then choose whether single sign-on is offered or required."
      footer={
        <Button disabled={!dirty || busy || (mode === 'required' && !anyEnabled)} onClick={() => void onSave({ auth: { sso: { mode, allowedDomains: domains, autoProvision, defaultRole, mfaExempt } } }, 'Single sign-on settings saved')}>
          Save single sign-on
        </Button>
      }
    >
      <div className="grid gap-3">
        {providers.map((p) => (
          <ProviderCard key={p.provider} config={p} />
        ))}
      </div>

      <div className="grid gap-2 border-t pt-4">
        <Label className="text-sm font-medium">Where single sign-on sits</Label>
        <ModeRadio name="sso-mode" value={mode} onChange={setMode} options={SSO_MODES} />
        {mode === 'required' && !anyEnabled ? <p className="text-destructive text-xs">Turn on at least one provider above before requiring single sign-on.</p> : null}
      </div>

      <div className="grid gap-4 border-t pt-4 sm:grid-cols-2">
        <Field label="Allowed email domains" htmlFor="sso-domains" description="Only accounts from these domains may sign in with SSO. Leave empty to allow any account the provider verifies.">
          <TagInput id="sso-domains" value={domains} onChange={(v) => setDomains(v.map((d) => d.trim().toLowerCase().replace(/^@/, '')).filter(Boolean))} placeholder="example.com and press Enter" />
        </Field>
        <div className="grid gap-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <Label htmlFor="sso-auto" className="text-sm font-medium">
                Create accounts automatically
              </Label>
              <p className="text-muted-foreground text-xs">Someone from an allowed domain who signs in for the first time gets an account without an invitation.</p>
            </div>
            <Switch id="sso-auto" checked={autoProvision} onCheckedChange={setAutoProvision} className="mt-0.5" />
          </div>
          {autoProvision ? (
            <Field label="Access level for new accounts" htmlFor="sso-role">
              <select id="sso-role" className={selectClass} value={defaultRole} onChange={(e) => setDefaultRole(e.target.value as typeof defaultRole)}>
                <option value="member">Project Manager (assigned to jobs later)</option>
                <option value="org_admin">Super User (runs the organization)</option>
              </select>
            </Field>
          ) : null}
          <div className="flex items-start justify-between gap-4">
            <div>
              <Label htmlFor="sso-mfa-exempt" className="text-sm font-medium">
                Trust the provider’s own two-step verification
              </Label>
              <p className="text-muted-foreground text-xs">People who sign in with SSO skip the app’s second step (Microsoft / Google enforce their own).</p>
            </div>
            <Switch id="sso-mfa-exempt" checked={mfaExempt} onCheckedChange={setMfaExempt} className="mt-0.5" />
          </div>
        </div>
      </div>
    </Section>
  );
}

function ProviderCard({ config }: { config: SsoProviderConfigDto }) {
  const update = useUpdateTenantSso();
  const [own, setOwn] = useState(!!config.clientId);
  const [clientId, setClientId] = useState(config.clientId ?? '');
  const [clientSecret, setClientSecret] = useState('');
  const [issuerTenant, setIssuerTenant] = useState(config.issuerTenant ?? '');
  useEffect(() => {
    setOwn(!!config.clientId);
    setClientId(config.clientId ?? '');
    setIssuerTenant(config.issuerTenant ?? '');
    setClientSecret('');
  }, [config.clientId, config.issuerTenant]);

  const toggle = async (enabled: boolean) => {
    try {
      await update.mutateAsync({ provider: config.provider, body: { enabled } });
      toast.success(`${config.label} sign-in ${enabled ? 'turned on' : 'turned off'}`);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const saveRegistration = async () => {
    try {
      await update.mutateAsync({
        provider: config.provider,
        body: own ? { clientId: clientId.trim(), ...(clientSecret.trim() ? { clientSecret: clientSecret.trim() } : {}), issuerTenant: issuerTenant.trim() || null } : { clientId: null, issuerTenant: issuerTenant.trim() || null },
      });
      toast.success(`${config.label} registration saved`);
      setClientSecret('');
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const canEnable = config.configured || (own && clientId.trim().length > 0);
  const microsoft = config.provider === 'microsoft';

  return (
    <div className="border">
      <div className="flex flex-wrap items-center gap-3 p-3">
        <ProviderIcon provider={config.provider} className="size-5" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
            {SSO_LABEL[config.provider]}
            {config.enabled && config.configured ? (
              <Badge variant="secondary">
                <CheckCircle2Icon aria-hidden /> On
              </Badge>
            ) : null}
          </div>
          <p className="text-muted-foreground text-xs">
            {config.source === 'own' ? 'Using your own app registration.' : config.source === 'platform' ? 'Using the Crewline platform registration; no setup needed on your side.' : 'Not configured yet: add your own app registration below.'}
          </p>
        </div>
        <Switch checked={config.enabled} disabled={update.isPending || (!config.enabled && !canEnable)} onCheckedChange={(v) => void toggle(v)} aria-label={`${SSO_LABEL[config.provider]} sign-in`} />
      </div>
      <details className="border-t" open={own && !config.clientId}>
        <summary className="text-muted-foreground hover:text-foreground cursor-pointer px-3 py-2 text-xs">Advanced: app registration and directory</summary>
        <div className="grid gap-4 px-3 pb-4 pt-2">
          <div className="flex items-start gap-3">
            <Checkbox id={`own-${config.provider}`} checked={own} onCheckedChange={(v) => setOwn(v === true)} className="mt-0.5" disabled={!config.platformAvailable && !!config.clientId} />
            <div className="grid gap-1">
              <Label htmlFor={`own-${config.provider}`} className="text-sm font-medium">
                Use our own {SSO_LABEL[config.provider]} app registration
              </Label>
              <p className="text-muted-foreground text-xs">
                {config.platformAvailable ? 'Optional. Otherwise Crewline’s registration is used.' : 'Required: the platform has no registration for this provider.'}{' '}
                <a href={config.setupUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline-offset-4 hover:underline">
                  Open {SSO_LABEL[config.provider]} <ExternalLinkIcon className="size-3" aria-hidden />
                </a>
              </p>
            </div>
          </div>
          <Field label="Redirect URI to register" htmlFor={`redir-${config.provider}`} description="Add this exact URL to the app registration’s web redirect URIs.">
            <div className="flex gap-2">
              <Input id={`redir-${config.provider}`} value={config.redirectUri} readOnly className="font-mono text-xs" />
              <CopyButton value={config.redirectUri} />
            </div>
          </Field>
          {own ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Client ID" htmlFor={`cid-${config.provider}`}>
                <Input id={`cid-${config.provider}`} value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder={microsoft ? 'Application (client) ID' : 'xxxx.apps.googleusercontent.com'} autoComplete="off" />
              </Field>
              <Field label="Client secret" htmlFor={`csec-${config.provider}`} description={config.clientSecretMasked ? `Stored: ${config.clientSecretMasked}. Leave blank to keep it.` : undefined}>
                <Input id={`csec-${config.provider}`} type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} placeholder={config.clientSecretMasked ? '••••••••' : 'Secret value'} autoComplete="new-password" />
              </Field>
            </div>
          ) : null}
          <Field
            label={microsoft ? 'Directory (Entra tenant)' : 'Google Workspace domain'}
            htmlFor={`iss-${config.provider}`}
            description={microsoft ? 'Your Entra tenant ID or verified domain, so only your directory’s accounts are accepted. Leave blank to accept any work or school account.' : 'Pre-selects accounts from this domain on the Google account picker (hosted domain). Optional.'}
          >
            <Input id={`iss-${config.provider}`} value={issuerTenant} onChange={(e) => setIssuerTenant(e.target.value)} placeholder={microsoft ? 'contoso.com or 00000000-0000-0000-0000-000000000000' : 'example.com'} autoComplete="off" />
          </Field>
          <div className="flex justify-end">
            <Button size="sm" variant="outline" disabled={update.isPending || (own && !clientId.trim())} onClick={() => void saveRegistration()}>
              Save registration
            </Button>
          </div>
        </div>
      </details>
    </div>
  );
}

// ---------------------------------------------------------------- two-step verification

const MFA_MODES: Array<{ value: MfaMode; label: string; hint: string }> = [
  { value: 'off', label: 'Off', hint: 'No second step. Existing enrollments are kept but not asked for.' },
  { value: 'optional', label: 'Optional', hint: 'People may turn it on for themselves under Account.' },
  { value: 'required', label: 'Required', hint: 'Everyone sets up a second step the next time they sign in.' },
];

const REMEMBER_OPTIONS = [
  { value: 0, label: 'Ask every time' },
  { value: 1, label: '1 day' },
  { value: 7, label: '7 days' },
  { value: 14, label: '14 days' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
];

function MfaSection({ view, onSave, busy }: { view: TenantSettingsView; onSave: SaveFn; busy: boolean }) {
  const policy = view.settings.auth.mfa;
  const [mode, setMode] = useState<MfaMode>(policy.mode);
  const [methods, setMethods] = useState(policy.methods);
  const [days, setDays] = useState(policy.rememberDeviceDays);
  useEffect(() => {
    setMode(policy.mode);
    setMethods(policy.methods);
    setDays(policy.rememberDeviceDays);
  }, [policy.mode, policy.methods, policy.rememberDeviceDays]);
  const dirty = mode !== policy.mode || days !== policy.rememberDeviceDays || methods.totp !== policy.methods.totp || methods.sms !== policy.methods.sms || methods.email !== policy.methods.email;
  const none = !methods.totp && !methods.sms && !methods.email;

  return (
    <Section
      title="Two-step verification"
      description="A second step at sign-in: a code from an authenticator app, a text message or an email. Applies to password and one-time-code sign-ins; single sign-on follows the switch above."
      footer={
        <Button disabled={!dirty || busy || none} onClick={() => void onSave({ auth: { mfa: { mode, methods, rememberDeviceDays: days } } }, 'Two-step verification settings saved')}>
          Save two-step verification
        </Button>
      }
    >
      <ModeRadio name="mfa-mode" value={mode} onChange={setMode} options={MFA_MODES} />
      <div className="grid gap-4 border-t pt-4 sm:grid-cols-2">
        <div className="grid gap-2">
          <Label className="text-sm font-medium">Allowed methods</Label>
          {(
            [
              ['totp', 'Authenticator app', 'Microsoft Authenticator, Google Authenticator, 1Password… Works offline; the most secure option.'],
              ['sms', 'Text message', 'A code texted from your organization’s number. Needs a phone number assigned to the organization.'],
              ['email', 'Email', 'A code emailed to the person’s work address.'],
            ] as const
          ).map(([key, label, hint]) => (
            <label key={key} htmlFor={`mfa-${key}`} className="flex cursor-pointer items-start gap-3 border p-3">
              <Checkbox id={`mfa-${key}`} checked={methods[key]} onCheckedChange={(v) => setMethods({ ...methods, [key]: v === true })} className="mt-0.5" />
              <span>
                <span className="block text-sm font-medium">{label}</span>
                <span className="text-muted-foreground block text-xs">{hint}</span>
              </span>
            </label>
          ))}
          {none ? <p className="text-destructive text-xs">Allow at least one method.</p> : null}
        </div>
        <div className="grid gap-4">
          <Field label="Remember a device for" htmlFor="mfa-days" description="After a successful second step, people may skip it on that browser for this long.">
            <select id="mfa-days" className={selectClass} value={days} onChange={(e) => setDays(Number(e.target.value))}>
              {REMEMBER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
          <div className="text-muted-foreground flex items-start gap-3 border border-dashed p-3 text-xs leading-relaxed">
            <ShieldCheckIcon className="mt-0.5 size-4 shrink-0" aria-hidden />
            <span>
              Everyone gets eight single-use recovery codes when they enroll. If someone loses their phone, a Super User can ask Crewline support to reset their second step, or they can use a recovery code.
            </span>
          </div>
          <div className="text-muted-foreground flex items-start gap-3 text-xs">
            <KeyRoundIcon className="mt-0.5 size-4 shrink-0" aria-hidden />
            <span>Your own second step lives under Account → Two-step verification.</span>
          </div>
        </div>
      </div>
    </Section>
  );
}

export type { SsoProviderId };
